import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  ArrowLeft,
  Clock,
  Sparkles,
  Calculator,
  Shield,
  Zap,
  CheckCircle
} from 'lucide-react';

// Calcule le nombre de mois restants avant le 1er septembre 2027 (obligation d'émission micro-entreprises)
const computeMonthsLeft = () => {
  const deadline = new Date('2027-09-01T00:00:00');
  const now = new Date();
  const diffMs = deadline - now;
  if (diffMs <= 0) return 0;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return { months: Math.max(1, Math.round(days / 30)), days };
};

const PreparerPage = () => {
  const [visible, setVisible] = useState(false);
  const countdown = computeMonthsLeft();

  useEffect(() => {
    // Fade-in au montage
    const t = setTimeout(() => setVisible(true), 80);
    window.scrollTo(0, 0);
    return () => clearTimeout(t);
  }, []);

  const monthsLabel = countdown === 0
    ? "L'échéance est passée — agissez maintenant"
    : countdown.months <= 1
      ? `Plus que ${countdown.days} jour${countdown.days > 1 ? 's' : ''}`
      : `Plus que ${countdown.months} mois`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-white flex flex-col">
      {/* Header glass */}
      <header className="sticky top-0 z-40 glass border-b border-emerald-100/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/conformite-2026" className="font-bold text-lg sm:text-xl font-['Manrope']" data-testid="preparer-home-link">
            <span className="text-emerald-600">Capsule</span> Patrimoine
          </Link>
          <Link to="/conformite-2026" className="text-xs sm:text-sm text-muted-foreground hover:text-emerald-700 inline-flex items-center gap-1" data-testid="preparer-back-link">
            <ArrowLeft className="h-3 w-3" /> Retour à la réforme
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16 w-full">
        <div
          className={`space-y-10 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          {/* Pill urgency */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-900 px-4 py-1.5 rounded-full text-sm font-medium" data-testid="preparer-urgency-pill">
              <Clock className="h-4 w-4" />
              {monthsLabel} avant l&apos;obligation d&apos;émission du 1<sup>er</sup> septembre 2027
            </div>
          </div>

          {/* Headline */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl sm:text-5xl font-bold font-['Manrope'] leading-tight" data-testid="preparer-h1">
              Vous préparer à la réforme,<br />
              <span className="text-emerald-600">en 5 minutes</span>.
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-xl mx-auto">
              Testez gratuitement Capsule Patrimoine et voyez comment automatiser votre gestion financière —{' '}
              <strong className="text-slate-800">tout en anticipant la réforme 2026</strong>.
            </p>
          </div>

          {/* 3 points clés */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
            {[
              { icon: Calculator, label: 'Calcul URSSAF + TVA instantané' },
              { icon: Shield, label: 'Conforme réforme 2026 anticipée' },
              { icon: Zap, label: 'Sans création de compte' }
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2 p-3 bg-white border border-emerald-100 rounded-lg shadow-sm">
                <item.icon className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-slate-700">{item.label}</span>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto pt-2">
            <Link to="/demo" className="flex-1" data-testid="preparer-cta-primary">
              <Button size="lg" className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-base font-bold shadow-lg shadow-emerald-200/50">
                <Sparkles className="h-5 w-5 mr-2" />
                Essayer la calculatrice (gratuit)
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
            <Link
              to="/achat"
              className="flex-1"
              data-testid="preparer-cta-secondary"
            >
              <Button size="lg" variant="outline" className="w-full h-14 border-2 border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white text-base font-semibold">
                Accéder directement au dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Rassurance */}
          <div className="text-center space-y-1 pt-2">
            <p className="text-xs text-slate-500 flex items-center justify-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-600" /> 100% gratuit pour la calculatrice</span>
              <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-600" /> Garantie 14 jours sur l&apos;achat</span>
              <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3 text-emerald-600" /> Paiement sécurisé PayPal</span>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-emerald-100/60 py-5 mt-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-xs text-muted-foreground">
          <Link to="/conformite-2026" className="text-emerald-700 hover:underline inline-flex items-center gap-1" data-testid="preparer-footer-back">
            <ArrowLeft className="h-3 w-3" /> Un doute ? Revoir le détail de la réforme
          </Link>
          <p className="mt-2">
            © 2026 Capsule Patrimoine · SIRET 484 280 771 00060 ·{' '}
            <a href="mailto:contact@capsulepatrimoine.com" className="hover:text-emerald-700">contact@capsulepatrimoine.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PreparerPage;
